import { z } from 'zod'

import type { BillListParams } from '@/features/bills/api'
import { SITUACAO_STATUS } from '@/features/bills/search'
import { paginacaoSearchFields, toLimitOffset } from '@/widgets/data-table/table-search'

import type { ServiceInvoiceListParams } from './service-invoices'

/**
 * Search params das duas telas de faturamento (Central de cobrança e NFS-e).
 *
 * Schemas separados porque os `status` e as allowlists de `order_by` divergem —
 * cobrança tem `sent` e ordena por vencimento; nota tem `issued` e não tem data
 * de emissão própria (a emissão real é do adapter, ainda não existe).
 */

// ------------------------------------------------------------------ cobranças

export const cobrancasSearchSchema = z.object({
  ...paginacaoSearchFields,
  q: z.string().min(1).optional(),
  // Período de VENCIMENTO (filtro da Nibo).
  de: z.iso.date().optional(),
  ate: z.iso.date().optional(),
  // Status = do recebível (o que a coluna mostra): Em aberto / Recebido.
  status: z.enum(['open', 'paid']).optional(),
  // Ordenação compatível com BillOrderBy (a lista é de bills — sem 'stakeholder',
  // que o /v1/bills composto não ordena). Default vencimento crescente, como na Nibo.
  ordenar: z
    .enum([
      'due_date',
      '-due_date',
      'amount',
      '-amount',
      'status',
      '-status',
      'created_at',
      '-created_at',
    ])
    .default('due_date'),
})

export type CobrancasSearch = z.infer<typeof cobrancasSearchSchema>

export const COBRANCAS_ORDEM_DEFAULT = 'due_date' satisfies CobrancasSearch['ordenar']

export const COBRANCAS_FILTROS = [
  'q',
  'de',
  'ate',
  'status',
] as const satisfies readonly (keyof CobrancasSearch)[]

/**
 * A Central de cobrança LISTA os RECEBÍVEIS (contas a receber das DUAS origens —
 * contábil E banco/Hatch) — como na Nibo, não só as cobranças explícitas — para
 * dar visibilidade do que há para cobrar (a emissão em si é evolução futura).
 * Usa a lista COMPOSTA `/v1/bills` com `direction: 'receivable'`. Status
 * open/paid vira a visão abertas/baixadas do bills; o filtro por Nome sai (o
 * `/v1/bills` não filtra por contato — a busca `q` casa por nome).
 */
export function toCobrancaBillParams(s: CobrancasSearch): BillListParams {
  const situacao = s.status === 'open' ? 'abertas' : s.status === 'paid' ? 'baixadas' : undefined
  return {
    direction: 'receivable',
    ...(situacao !== undefined && { status: SITUACAO_STATUS[situacao] }),
    ...(s.de !== undefined && { due_after: s.de }),
    ...(s.ate !== undefined && { due_before: s.ate }),
    ...(s.q !== undefined && { q: s.q }),
    order_by: s.ordenar,
    ...toLimitOffset(s),
  }
}

// ----------------------------------------------------------------- notas NFSe

export const nfseSearchSchema = z.object({
  ...paginacaoSearchFields,
  q: z.string().min(1).optional(),
  // 'canceled' saiu do vocabulário da UI — não é opção de filtro.
  status: z.enum(['pending', 'issued']).optional(),
  ordenar: z
    .enum([
      'created_at',
      '-created_at',
      'status',
      '-status',
      'amount',
      '-amount',
      'description',
      '-description',
      'stakeholder',
      '-stakeholder',
    ])
    .default('-created_at'),
})

export type NfseSearch = z.infer<typeof nfseSearchSchema>

/** Default do backend (service_invoice_service._ORDER_BY_DEFAULT). */
export const NFSE_ORDEM_DEFAULT = '-created_at' satisfies NfseSearch['ordenar']

export const NFSE_FILTROS = ['q', 'status'] as const satisfies readonly (keyof NfseSearch)[]

export function toServiceInvoiceListParams(s: NfseSearch): ServiceInvoiceListParams {
  return {
    ...(s.q !== undefined && { search: s.q }),
    ...(s.status !== undefined && { status: s.status }),
    order_by: s.ordenar,
    ...toLimitOffset(s),
  }
}
